import { ComponentProps, FC, useCallback, useState } from "react"
import { IIncomeCategory } from "src/domain/entities"
import { incomeCategoryUpdateService } from "src/domain/services/incomeCategory/incomeCategoryUpdateService"
import { fieldAddService } from "src/domain/services/field/fieldAddService"
import { fieldDeleteService } from "src/domain/services/field/fieldDeleteService"
import { fieldUpdateService } from "src/domain/services/field/fieldUpdateService"
import { useAuth } from "src/hooks"
import { FieldType } from "src/utils/enums"
import { FormikOnSubmit } from "src/utils/types"
import { IncomeCategoryUpdateDrawerFormView, IIncomeCategoryUpdateFormFields } from "."

type Props = Omit<
	ComponentProps<typeof IncomeCategoryUpdateDrawerFormView>,
	| "handleSubmit"
	| "fields"
	| "setFields"
	| "handleFieldAdd"
	| "handleFieldUpdate"
	| "handleFieldDelete"
> & {
	onSuccess?: (incomeCategory: IIncomeCategory) => void
}

export const IncomeCategoryUpdateDrawerFormController: FC<Props> = ({
	incomeCategory,
	onSuccess,
	...rest
}) => {
	const { token } = useAuth()

	const handleSubmit = useCallback<FormikOnSubmit<IIncomeCategoryUpdateFormFields>>(
		async (values, { setErrors }) => {
			try {
				const updatedIncomeCategory = await incomeCategoryUpdateService(
					{ id: incomeCategory.id, update: values },
					token,
				)
				onSuccess && onSuccess(updatedIncomeCategory)
				rest.onClose()
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Something went wrong"
				setErrors({ name: message })
			}
		},
		[onSuccess, rest, token, incomeCategory.id],
	)

	const [fields, setFields] = useState(
		incomeCategory.fields?.sort((a, b) => a.position - b.position) ?? [],
	)

	const handleFieldAdd = useCallback(async () => {
		const field = await fieldAddService(
			{
				type: FieldType.STRING,
				name: "",
				position: fields.length + 1,
				categoryId: incomeCategory.id,
				categoryType: "income",
			},
			token,
		)

		setFields([...fields, field])
	}, [fields, incomeCategory.id, token])

	const handleFieldUpdate = useCallback(
		async (id: string, update: { name?: string; type?: FieldType }) => {
			await fieldUpdateService({ id, update }, token)
			setFields(
				fields.map((field) =>
					field.id === id ? { ...field, ...update } : field,
				),
			)
		},
		[fields, token],
	)

	const handleFieldDelete = useCallback(
		async (index) => {
			await fieldDeleteService({ id: fields[index].id }, token)

			setFields(fields.filter((_, i) => i !== index))
		},
		[fields, token],
	)

	return (
		<IncomeCategoryUpdateDrawerFormView
			incomeCategory={incomeCategory}
			fields={fields}
			setFields={setFields}
			handleSubmit={handleSubmit}
			handleFieldAdd={handleFieldAdd}
			handleFieldUpdate={handleFieldUpdate}
			handleFieldDelete={handleFieldDelete}
			{...rest}
		/>
	)
}
